import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  fetchAllScheduledPipelineEnrichedProducts,
  fetchScheduledPipelineEnrichedProducts,
} from '../services/scheduled-enrichment.service';

/**
 * Fetches a page of enriched products from a completed scheduled workflow
 * (sample-enrichment or full-catalog pipeline). Disabled when `workflowId`
 * is absent so the hook is safe to call before the trigger resolves.
 */
export function useScheduledEnrichedProducts(workflowId: string | null, page = 0, size = 50) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.pipelineEnrichedProducts(
      workflowId ?? '__disabled__',
      page,
      size,
    ),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId required');
      return fetchScheduledPipelineEnrichedProducts(workflowId, page, size);
    },
    enabled: !!workflowId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Every enriched product of a completed scheduled pipeline run, all server pages
 * folded into one response. For the listings that filter and paginate client-side;
 * `useScheduledEnrichedProducts` stays the server-paged read.
 */
export function useAllScheduledEnrichedProducts(workflowId: string | null) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.pipelineEnrichedProductsComplete(
      workflowId ?? '__disabled__',
    ),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId required');
      return fetchAllScheduledPipelineEnrichedProducts(workflowId);
    },
    enabled: !!workflowId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
